import React, { useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Slider, Divider,
} from '@mui/material';
import PropTypes from 'prop-types';
import {
  detectEntries, scoreEntries, summariseEntries, alignSeriesToBars,
  RANGE_WINDOW, FLAT_TOLERANCE, FLAT_TOLERANCE_MIN, FLAT_TOLERANCE_MAX,
  MIN_SLOPE, MIN_SLOPE_MIN, MIN_SLOPE_MAX, ENTRY_COLORS,
} from '../utils/trendPulseEntries';

/**
 * The entry-shelf detector: two thresholds, a live count, and draw it.
 *
 * <h2>The count updates as the sliders move, not on Apply</h2>
 *
 * Both thresholds are fractions of a rolling range, so what they mean on this chart cannot be
 * guessed from the numbers — 0.75% of what, exactly, depends on how the curve has been behaving.
 * The count is the only honest feedback, and making it wait for Apply would mean drawing every
 * candidate setting in turn to find out which was worth drawing.
 */
const PatternPredictionDialog = ({ open, onClose, barData, trendPulseSeries, onApply }) => {
  const [flatPct, setFlatPct] = useState(FLAT_TOLERANCE);
  const [slopePct, setSlopePct] = useState(MIN_SLOPE);

  const values = useMemo(
    () => alignSeriesToBars(trendPulseSeries, barData?.dates),
    [trendPulseSeries, barData]);
  const hasSeries = values.some(v => Number.isFinite(v));

  const { entries, stats } = useMemo(() => {
    if (!open || !hasSeries) return { entries: [], stats: null };
    const found = scoreEntries(
      detectEntries(values, { flatPct, slopePct }), barData?.ohlc);
    return { entries: found, stats: summariseEntries(found) };
  }, [open, hasSeries, values, barData, flatPct, slopePct]);

  const asPct = v => `${(v * 100).toFixed(2).replace(/0$/, '')}%`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ fontWeight: 500 }}>TrendPulse Entry Signals</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ fontSize: 12, opacity: 0.8, mb: 2 }}>
          {/* The rule, stated where the thresholds are set: the sliders are meaningless without it,
              and a reader who cannot see what they are adjusting cannot judge the count. */}
          An entry triggers at the close of bar <b>t</b> when the curve makes a decisive move into{' '}
          <b>P[t-1]</b> and then stops: <b>|P[t] − P[t-1]| ≤ ε</b> after{' '}
          <b>|P[t-1] − P[t-2]| ≥ Δ</b>. The call is the opposite of the thrust that stalled — a fall
          that goes flat is bullish. Both thresholds are fractions of the rolling{' '}
          {RANGE_WINDOW}-bar range, so they mean the same thing in a quiet stretch as in a violent
          one.
        </Box>

        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
          Flat Tolerance (ε_flat) — {asPct(flatPct)}
        </Typography>
        <Typography sx={{ fontSize: 12, opacity: 0.7, mb: 0.5 }}>
          How level the two-point stall has to be. Wider admits more.
        </Typography>
        <Slider
          size="small"
          value={flatPct}
          min={FLAT_TOLERANCE_MIN}
          max={FLAT_TOLERANCE_MAX}
          step={0.0005}
          marks={[{ value: FLAT_TOLERANCE_MIN, label: asPct(FLAT_TOLERANCE_MIN) },
            { value: FLAT_TOLERANCE, label: 'default' },
            { value: FLAT_TOLERANCE_MAX, label: asPct(FLAT_TOLERANCE_MAX) }]}
          onChange={(_, v) => setFlatPct(v)}
          sx={{ mb: 2 }}
        />

        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
          Minimum Incoming Slope (Δ_slope) — {asPct(slopePct)}
        </Typography>
        <Typography sx={{ fontSize: 12, opacity: 0.7, mb: 0.5 }}>
          How strong the move into the stall has to be. Wider admits fewer.
        </Typography>
        <Slider
          size="small"
          value={slopePct}
          min={MIN_SLOPE_MIN}
          max={MIN_SLOPE_MAX}
          step={0.0025}
          marks={[{ value: MIN_SLOPE_MIN, label: asPct(MIN_SLOPE_MIN) },
            { value: MIN_SLOPE, label: 'default' },
            { value: MIN_SLOPE_MAX, label: asPct(MIN_SLOPE_MAX) }]}
          onChange={(_, v) => setSlopePct(v)}
        />

        <Divider sx={{ my: 2 }} />

        {!hasSeries && (
          <Typography sx={{ color: '#b26a00', fontSize: 13 }}>
            Entries are detected on the TrendPulse curve, so TrendPulse has to be on the chart
            first. Run it, then reopen this dialog.
          </Typography>
        )}

        {/* Guarded on `stats`, not on `hasSeries`, and the difference is a crash.
            React runs this whole function to build the element tree BEFORE MUI decides whether the
            dialog is mounted, so the body renders while `open` is false — and the memo above
            short-circuits to null stats in exactly that case. Keyed on hasSeries, a chart with a
            TrendPulse curve loaded threw `Cannot read properties of null` on every render of the
            closed dialog. */}
        {hasSeries && stats && (
          <Box>
            <Typography sx={{ fontSize: 22, fontWeight: 700 }}>
              {stats.total} entr{stats.total === 1 ? 'y' : 'ies'}
              <Typography component="span" sx={{ fontSize: 14, opacity: 0.75, ml: 1 }}>
                across {values.length} bars
              </Typography>
            </Typography>
            <Typography sx={{ fontSize: 13, mt: 0.5 }}>
              <span style={{ color: ENTRY_COLORS.up, fontWeight: 600 }}>
                {stats.bullish} bullish
              </span>
              {' · '}
              <span style={{ color: ENTRY_COLORS.down, fontWeight: 600 }}>
                {stats.bearish} bearish
              </span>
            </Typography>

            {/* Not asked for, and kept short: the count is the study's output, but a count with no
                idea whether it was right is not something anyone can act on. */}
            <Typography sx={{ fontSize: 12, opacity: 0.75, mt: 1 }}>
              Forward price move in the signal&apos;s own direction:{' '}
              {stats.horizons.map((n, i) => {
                const h = stats.perHorizon[n];
                return (
                  <span key={n}>
                    {i > 0 ? ' · ' : ''}
                    {n} bars {h.successRate === null ? '—' : `${h.successRate.toFixed(1)}%`}
                  </span>
                );
              })}
              {stats.perHorizon[stats.horizons[0]].unresolved > 0
                && ` (${stats.perHorizon[stats.horizons[0]].unresolved} too recent to score)`}
            </Typography>

            {stats.total === 0 && (
              <Typography sx={{ mt: 1, fontSize: 13, opacity: 0.7 }}>
                Nothing at these thresholds. That is an answer about this chart, not an error —
                widen ε, or lower Δ.
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={onClose}>Close</Button>
        {/* No count without a curve to count on. "Draw 0 entries" is a statement about this
            symbol, and with TrendPulse not yet run it would be a statement about the wrong thing —
            the same confusion the message above the button exists to correct. */}
        <Button variant="contained" disabled={!entries.length}
          onClick={() => { onApply(entries, values); onClose(); }}>
          {hasSeries
            ? `Draw ${entries.length} entr${entries.length === 1 ? 'y' : 'ies'}`
            : 'Draw entries'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

PatternPredictionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  barData: PropTypes.object,
  /** The TrendPulse overlay's own data — where the stalls are found. */
  trendPulseSeries: PropTypes.array,
  onApply: PropTypes.func.isRequired,
};

export default PatternPredictionDialog;
